import { createContext, Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ENVS, IS_LOCAL, get, initials, put, signOut, type AdminMe, type AdminSchema, type Env } from '../lib/api';
import { useMobile } from '../lib/hooks';
import { useStored, type Density, type Theme } from '../lib/store';
import { Icon } from './Icons';
import { Field, type Severity } from './Block';

export interface Screen { key: string; label: string; group: string; icon: string; mobile?: boolean; sub?: string; hidden?: boolean }

export const SCREENS: Screen[] = [
  { key: 'overview', label: 'Inbox', group: 'OPERATE', icon: 'grid', mobile: true },
  { key: 'support', label: 'Support', group: 'OPERATE', icon: 'chat', mobile: true },
  { key: 'notifications', label: 'Notifications', group: 'OPERATE', icon: 'warn', mobile: true },
  { key: 'customers', label: 'Customers', group: 'CUSTOMERS', icon: 'users', mobile: true },
  { key: 'data', label: 'Transcriptions', group: 'CUSTOMERS', icon: 'signal', mobile: true },
  { key: 'jobs', label: 'Jobs', group: 'CUSTOMERS', icon: 'queue', mobile: true },
  { key: 'billing', label: 'Billing', group: 'CUSTOMERS', icon: 'card' },
  { key: 'costs', label: 'Running costs', group: 'CUSTOMERS', icon: 'chart' },
  { key: 'storage', label: 'Storage', group: 'CUSTOMERS', icon: 'stack' },
  { key: 'engines', label: 'Engines', group: 'FLEET', icon: 'cpu', mobile: true },
  { key: 'gpu-pool', label: 'GPU pool', group: 'FLEET', icon: 'server' },
  { key: 'gpu-types', label: 'GPU types', group: 'FLEET', icon: 'cpu' },
  { key: 'simulator', label: 'GPU pool simulator', group: 'FLEET', icon: 'chart' },
  { key: 'voices', label: 'Voices', group: 'FLEET', icon: 'speaker' },
  { key: 'api', label: 'API app', group: 'FLEET', icon: 'server' },
  { key: 'releases', label: 'Releases', group: 'FLEET', icon: 'tag' },
  { key: 'client-versions', label: 'Client versions', group: 'FLEET', icon: 'check' },
  { key: 'analytics', label: 'Analytics', group: 'RECORDS', icon: 'chart' },
  { key: 'audit', label: 'Audit log', group: 'RECORDS', icon: 'clip', mobile: true },
  { key: 'ask', label: 'Assistant', group: 'RECORDS', icon: 'chat', mobile: true },
  { key: 'articles', label: 'Articles', group: 'RECORDS', icon: 'doc' },
  { key: 'settings', label: 'Settings', group: 'RECORDS', icon: 'cog' },
];
const ALIASES: Record<string, string> = { users: 'customers', tts: 'voices' };

export interface AttentionItem { id: string; severity: Severity; kind: string; title: string; detail: string; at: string | null; n?: number; link: { screen: string; params?: Record<string, string>; drawer?: string } }
export interface Attention {
  items: AttentionItem[];
  counts: { action?: number; watch?: number; support?: number; supportUnread?: number; notifications?: number; notificationErrors?: number; jobsFailed?: number; purgeDue?: number; webhooksFailed?: number };
  ageS: number;
  asleep: boolean;
  hidden?: number;
  error?: string;
}
export interface View { id: string; screen: string; name: string; params: Record<string, string> }

export interface Toast { text: ReactNode; sub?: ReactNode; logged?: boolean; undo?: () => void; until: number }
export interface DrawerSpec { key: string; node: ReactNode }

export type DrawerRoute = (rest: string) => ReactNode | null | undefined;

export interface AdminCtx {
  env: Env;
  envs: Env[];
  setEnv: (key: string) => void;
  me: AdminMe;
  theme: Theme;
  setTheme: (t: Theme) => void;
  density: Density;
  mobile: boolean;
  paused: boolean;
  screen: string;
  params: Record<string, string>;
  go: (screen: string, params?: Record<string, string>) => void;
  setParams: (params: Record<string, string>) => void;
  open: (screen: string, drawer?: string, params?: Record<string, string>) => void;
  attention: Attention | null;
  attentionAt: number | null;
  reloadAttention: () => void;
  views: View[];
  saveViews: (views: View[]) => Promise<void>;
  search: string;
  setSearch: (q: string) => void;
  openDrawer: (key: string, node: ReactNode, push?: boolean) => void;
  closeDrawer: () => void;
  backDrawer: () => void;
  swapDrawer: (key: string, node: ReactNode) => void;
  drawerKey: string | null;
  addDrawerRoute: (prefix: string, route: DrawerRoute) => () => void;
  retryDrawers: () => void;
  openModal: (node: ReactNode) => void;
  closeModal: () => void;
  toast: (text: ReactNode, sub?: ReactNode, opts?: { logged?: boolean; undo?: () => void }) => void;
  setBadge: (screen: string, n: number) => void;
}

const Ctx = createContext<AdminCtx | null>(null);
export function useAdmin(): AdminCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAdmin outside the shell');
  return c;
}

function parseHash(): { screen: string; params: Record<string, string>; query: string; drawers: string[] } {
  const h = window.location.hash.replace(/^#\/?/, '');
  const at = h.indexOf('?');
  const [path, query] = at < 0 ? [h, ''] : [h.slice(0, at), h.slice(at + 1)];
  const [raw, ...drawers] = path.split('/');
  const screen = ALIASES[raw] ?? raw;
  const params: Record<string, string> = {};
  new URLSearchParams(query).forEach((v, k) => { params[k] = v; });
  return { screen: SCREENS.some((s) => s.key === screen) ? screen : 'overview', params, query, drawers: drawers.filter(Boolean).map((k) => { try { return decodeURIComponent(k); } catch { return k; } }) };
}
const hashOf = (screen: string, drawers: string[], query: string) => `#/${[screen, ...drawers.map((k) => encodeURIComponent(k).replace(/%3A/gi, ':'))].join('/')}${query ? `?${query}` : ''}`;
const sameKeys = (a: string[], b: string[]) => a.length === b.length && a.every((k, i) => k === b[i]);
const sameParams = (a: Record<string, string>, b: Record<string, string>) => Object.keys(a).length === Object.keys(b).length && Object.keys(a).every((k) => a[k] === b[k]);

export const linkedDrawers = (): string[] => parseHash().drawers;

export function useDrawerRoute(prefix: string, resolve: DrawerRoute, deps: unknown[]) {
  const ctx = useAdmin();
  const ref = useRef(resolve);
  ref.current = resolve;
  const { addDrawerRoute, retryDrawers } = ctx;
  useEffect(() => addDrawerRoute(prefix, (rest) => ref.current(rest)), [prefix, addDrawerRoute]);
  useEffect(() => { retryDrawers(); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useFilters<T extends Record<string, string>>(defaults: T): [T, (patch: Partial<T>) => void] {
  const { params, setParams } = useAdmin();
  const f = { ...defaults } as T;
  for (const k of Object.keys(defaults) as (keyof T)[]) if (typeof params[k as string] === 'string') f[k] = params[k as string] as T[keyof T];
  const set = useCallback((patch: Partial<T>) => {
    const next: Record<string, string> = { ...params };
    for (const [k, v] of Object.entries(patch)) { if (v === undefined || v === defaults[k]) delete next[k]; else next[k] = v as string; }
    setParams(next);
  }, [params, setParams]); // eslint-disable-line react-hooks/exhaustive-deps
  return [f, set];
}

export function sortOf(f: { sort: string; dir: string }, set: (p: { sort?: string; dir?: string }) => void, fallback: { key: string; dir: 'asc' | 'desc' }) {
  const sort = { key: f.sort || fallback.key, dir: (f.dir || (f.sort ? 'desc' : fallback.dir)) as 'asc' | 'desc' };
  return {
    sort,
    onSort: (key: string, firstDir: 'asc' | 'desc') => set(key === sort.key ? { sort: key, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { sort: key, dir: firstDir }),
  };
}

const IDLE_PAUSE_MS = 30 * 60_000;

export function Shell({ env, setEnv, me, theme, setTheme, children, signedOut, drawerRoutes }: { env: Env; setEnv: (k: string) => void; me: AdminMe; theme: Theme; setTheme: (t: Theme) => void; children: (ctx: AdminCtx) => ReactNode; signedOut: () => void; drawerRoutes: Record<string, DrawerRoute> }) {
  const mobile = useMobile();
  const [density, setDensity] = useStored<Density>('vs.admin.density', 'comfortable');
  const [route, setRoute] = useState(() => { const p = parseHash(); return { screen: p.screen, params: p.params }; });
  const [search, setSearch] = useState('');
  const [drawers, setDrawers] = useState<DrawerSpec[]>([]);
  const [modal, setModal] = useState<ReactNode | null>(null);
  const [toastV, setToastV] = useState<Toast | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const [envOpen, setEnvOpen] = useState(false);
  const [pausedBy, setPausedBy] = useState<'' | 'hidden tab' | 'idle' | 'you'>('');
  const [badges, setBadges] = useState<Record<string, number>>({});
  const [, setTick] = useState(0);
  const lastActive = useRef(Date.now());
  const searchRef = useRef<HTMLInputElement>(null);

  const drawersRef = useRef(drawers);
  drawersRef.current = drawers;
  const pending = useRef<string[] | null>(null);
  const linked = useRef(true);
  if (linked.current) { linked.current = false; const first = parseHash().drawers; if (first.length) pending.current = first; }
  const replaceNext = useRef(false);
  const synced = useRef(drawers);
  const screenNow = useRef(route.screen);
  screenNow.current = route.screen;
  const screenRoutes = useRef(new Map<string, DrawerRoute>());
  const [tries, setTries] = useState(0);
  const retryDrawers = useCallback(() => { if (pending.current) setTries((t) => t + 1); }, []);
  const addDrawerRoute = useCallback((prefix: string, r: DrawerRoute) => {
    screenRoutes.current.set(prefix, r);
    retryDrawers();
    return () => { if (screenRoutes.current.get(prefix) === r) screenRoutes.current.delete(prefix); };
  }, [retryDrawers]);

  useEffect(() => {
    const on = () => {
      const p = parseHash();
      setRoute((r) => (r.screen === p.screen && sameParams(r.params, p.params) ? r : { screen: p.screen, params: p.params }));
      setModal(null);
      if (sameKeys(drawersRef.current.map((d) => d.key), p.drawers)) { pending.current = null; return; }
      if (p.screen !== screenNow.current) { drawersRef.current = []; setDrawers([]); }
      pending.current = p.drawers;
      setTries((t) => t + 1);
    };
    window.addEventListener('hashchange', on);
    window.addEventListener('popstate', on);
    return () => { window.removeEventListener('hashchange', on); window.removeEventListener('popstate', on); };
  }, []);
  const go = useCallback((screen: string, params?: Record<string, string>) => {
    const q = new URLSearchParams(params ?? {}).toString();
    window.location.hash = `/${screen}${q ? `?${q}` : ''}`;
    setDrawers([]);
    setNavOpen(false);
    setEnvOpen(false);
    document.querySelector('.adm__scroll')?.scrollTo({ top: 0 });
  }, []);
  const setParams = useCallback((params: Record<string, string>) => {
    const cur = parseHash();
    const q = new URLSearchParams(params).toString();
    window.history.replaceState(null, '', hashOf(cur.screen, cur.drawers, q));
    setRoute((r) => (sameParams(r.params, params) ? r : { screen: r.screen, params }));
  }, []);
  const open = useCallback((screen: string, drawer?: string, params?: Record<string, string>) => {
    const q = new URLSearchParams(params ?? {}).toString();
    setNavOpen(false);
    setEnvOpen(false);
    window.location.hash = hashOf(screen, drawer ? [drawer] : [], q);
  }, []);

  useEffect(() => {
    const vis = () => { if (document.visibilityState === 'hidden') setPausedBy((p) => p || 'hidden tab'); else setPausedBy((p) => (p === 'hidden tab' ? '' : p)); };
    const act = () => { lastActive.current = Date.now(); setPausedBy((p) => (p === 'idle' ? '' : p)); };
    const idle = setInterval(() => { if (Date.now() - lastActive.current > IDLE_PAUSE_MS) setPausedBy((p) => p || 'idle'); setTick((t) => t + 1); }, 10_000);
    document.addEventListener('visibilitychange', vis);
    ['mousemove', 'keydown', 'pointerdown', 'scroll'].forEach((e) => window.addEventListener(e, act, { passive: true }));
    return () => { clearInterval(idle); document.removeEventListener('visibilitychange', vis); ['mousemove', 'keydown', 'pointerdown', 'scroll'].forEach((e) => window.removeEventListener(e, act)); };
  }, []);

  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement)?.tagName === 'INPUT' || (e.target as HTMLElement)?.tagName === 'TEXTAREA' || (e.target as HTMLElement)?.tagName === 'SELECT';
      if ((e.key === '/' && !typing) || (e.key === 'k' && (e.metaKey || e.ctrlKey))) { e.preventDefault(); if (mobile) setMobileSearch(true); searchRef.current?.focus(); }
      if (e.key === 'Escape') { if (modal) setModal(null); else if (drawers.length) setDrawers((d) => d.slice(0, -1)); else { setEnvOpen(false); setNavOpen(false); } }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [modal, drawers.length, mobile]);

  useEffect(() => {
    if (!toastV) return;
    const id = setTimeout(() => setToastV(null), Math.max(0, toastV.until - Date.now()));
    return () => clearTimeout(id);
  }, [toastV]);

  const toast = useCallback((text: ReactNode, sub?: ReactNode, opts?: { logged?: boolean; undo?: () => void }) => {
    setToastV({ text, sub, logged: opts?.logged ?? true, undo: opts?.undo, until: Date.now() + 10_000 });
  }, []);
  const openDrawer = useCallback((key: string, node: ReactNode, push?: boolean) => { pending.current = null; setDrawers((d) => (push ? [...d, { key, node }] : [{ key, node }])); }, []);
  const closeDrawer = useCallback(() => { pending.current = null; setDrawers([]); }, []);
  const backDrawer = useCallback(() => { pending.current = null; setDrawers((d) => d.slice(0, -1)); }, []);
  const swapDrawer = useCallback((key: string, node: ReactNode) => { pending.current = null; replaceNext.current = true; setDrawers((d) => [...d.slice(0, -1), { key, node }]); }, []);
  const openModal = useCallback((node: ReactNode) => setModal(node), []);
  const closeModal = useCallback(() => setModal(null), []);
  const setBadge = useCallback((screen: string, n: number) => setBadges((b) => (b[screen] === n ? b : { ...b, [screen]: n })), []);

  const paused = pausedBy !== '';

  const [attention, setAttention] = useState<Attention | null>(null);
  const [attentionAt, setAttentionAt] = useState<number | null>(null);
  const loadAttention = useCallback((fresh: boolean) => {
    get<Attention>(env, `/admin/attention${fresh ? '?wake=true' : ''}`).then((a) => { setAttention(a); setAttentionAt(Date.now()); }).catch(() => {});
  }, [env]);
  const reloadAttention = useCallback(() => loadAttention(true), [loadAttention]);
  useEffect(() => { setAttention(null); loadAttention(false); }, [loadAttention]);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => loadAttention(false), 30_000);
    return () => clearInterval(id);
  }, [paused, reloadAttention]);
  const c = attention?.counts;
  const navBadge: Record<string, { n: number; watch?: boolean }> = {
    overview: c?.action ? { n: c.action } : c?.watch ? { n: c.watch, watch: true } : { n: 0 },
    support: { n: c?.support ?? badges.support ?? 0 },
    notifications: { n: c?.notifications ?? badges.notifications ?? 0 },
    jobs: { n: c?.jobsFailed ?? badges.jobs ?? 0 },
    customers: { n: c?.purgeDue ?? 0 },
    billing: { n: c?.webhooksFailed ?? 0 },
  };

  const [views, setViews] = useState<View[]>([]);
  useEffect(() => { setViews([]); get<{ views: View[] }>(env, '/admin/views').then((v) => setViews(v.views ?? [])).catch(() => {}); }, [env]);
  const saveViews = useCallback(async (next: View[]) => {
    const r = await put<{ views: View[] }>(env, '/admin/views', { views: next });
    setViews(r.views ?? []);
  }, [env]);
  const [schema, setSchema] = useState<AdminSchema | undefined>(me.schema);
  useEffect(() => { setSchema(me.schema); }, [me]);
  useEffect(() => {
    if (paused) return;
    let live = true;
    const id = setInterval(() => { get<AdminMe>(env, '/admin/me').then((m) => { if (live) setSchema(m.schema); }).catch(() => {}); }, 60_000);
    return () => { live = false; clearInterval(id); };
  }, [env, paused]);

  const ctx = useMemo<AdminCtx>(() => ({
    env, envs: ENVS, setEnv, me, theme, setTheme, density, mobile, paused, screen: route.screen, params: route.params, go, setParams, open, search, setSearch,
    openDrawer, closeDrawer, backDrawer, swapDrawer, drawerKey: drawers[drawers.length - 1]?.key ?? null, addDrawerRoute, retryDrawers, openModal, closeModal, toast, setBadge,
    attention, attentionAt, reloadAttention, views, saveViews,
  }), [env, setEnv, me, theme, setTheme, density, mobile, paused, route, go, setParams, open, search, openDrawer, closeDrawer, backDrawer, swapDrawer, drawers, addDrawerRoute, retryDrawers, openModal, closeModal, toast, setBadge, attention, attentionAt, reloadAttention, views, saveViews]);

  useEffect(() => {
    if (pending.current || synced.current === drawers) return;
    synced.current = drawers;
    const keys = drawers.map((d) => d.key);
    const cur = parseHash();
    const replace = replaceNext.current;
    replaceNext.current = false;
    if (sameKeys(keys, cur.drawers)) return;
    const url = hashOf(cur.screen, keys, cur.query);
    if (replace) window.history.replaceState(null, '', url); else window.history.pushState(null, '', url);
  }, [drawers]);

  useEffect(() => {
    const want = pending.current;
    if (!want) return;
    const have = drawersRef.current;
    const out: DrawerSpec[] = [];
    for (const [i, k] of want.entries()) {
      if (have[i]?.key === k && out.length === i) { out.push(have[i]); continue; }
      const prefix = [...screenRoutes.current.keys(), ...Object.keys(drawerRoutes)].filter((p) => k.startsWith(`${p}:`)).sort((a, b) => b.length - a.length)[0];
      const r = prefix ? (screenRoutes.current.get(prefix) ?? drawerRoutes[prefix]) : undefined;
      const node = r ? r(k.slice(prefix!.length + 1)) : null;
      if (node === undefined) return;
      if (node === null) break;
      out.push({ key: k, node });
    }
    pending.current = null;
    replaceNext.current = out.length !== want.length;
    setDrawers(out);
  }, [tries, drawerRoutes]);

  const envVar = env.key === 'production' ? 'prod' : env.key === 'e2e' ? 'e2e' : 'local';
  const style = { ['--env-solid' as string]: `var(--env-${envVar})`, ['--env-ink' as string]: `var(--env-${envVar}-ink)`, ['--env-bar' as string]: `color-mix(in oklab, var(--env-${envVar}) 14%, var(--vs-page))`, ['--env-line' as string]: `color-mix(in oklab, var(--env-${envVar}) 45%, transparent)` };
  const cur = SCREENS.find((s) => s.key === route.screen) ?? SCREENS[0];
  const groups = Array.from(new Set(SCREENS.map((s) => s.group)));
  const top = drawers[drawers.length - 1];

  const nav = (
    <>
      <div className="adm__brand">
        <img src={theme === 'dark' ? '/vocalstack-logo-dark.svg' : '/vocalstack-logo.svg'} alt="VocalStack" style={{ width: 118, height: 'auto', display: 'block' }} />
        <span className="adm__tag">ADMIN</span>
      </div>
      <nav className="adm__groups" aria-label="Screens">
        {groups.map((g) => (
          <div key={g} style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <div className="adm__group-label">{g}</div>
            {SCREENS.filter((s) => s.group === g && !s.hidden).map((s) => {
              const b = navBadge[s.key] ?? { n: badges[s.key] ?? 0 };
              const mine = views.filter((v) => v.screen === s.key);
              const onView = mine.find((v) => route.screen === s.key && sameParams(v.params, route.params));
              return (
                <Fragment key={s.key}>
                  <button type="button" className={`adm__item ${route.screen === s.key && !onView ? 'adm__item--on' : ''}`} aria-current={route.screen === s.key ? 'page' : undefined} onClick={() => go(s.key)}>
                    <Icon name={s.icon} size={17} stroke={1.5} />
                    <span>{s.label}</span>
                    {mobile && !s.mobile && <span style={{ flex: 'none', font: '600 8.5px var(--vs-font-mono)', letterSpacing: '.08em', color: 'var(--ink-45)' }}>READ-ONLY</span>}
                    {b.n ? <span className={`adm__badge ${b.watch ? 'adm__badge--watch' : ''}`}>{b.n}</span> : null}
                  </button>
                  {mine.map((v) => (
                    <div key={v.id} className={`adm__view ${onView?.id === v.id ? 'adm__view--on' : ''}`}>
                      <button type="button" style={{ display: 'contents' }} onClick={() => go(s.key, v.params)} title={`Saved view: ${v.name}`}><span>{v.name}</span></button>
                      <button type="button" className="adm__view-x" aria-label={`Remove the view ${v.name}`} title="Remove this view" onClick={() => { void saveViews(views.filter((x) => x.id !== v.id)).then(() => toast(`Removed the view “${v.name}”`, undefined, { logged: false })); }}><Icon name="close" size={11} /></button>
                    </div>
                  ))}
                </Fragment>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="adm__foot">
        <div className="adm__avatar">{initials(me.admin.name || me.admin.email)}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink-80)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{me.admin.name || me.admin.email}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, minWidth: 0 }}>
            <span className="adm__tag" style={{ color: 'var(--ink-70)', letterSpacing: '.08em' }}>ADMIN</span>
            <span style={{ font: '600 9px var(--vs-font-mono)', letterSpacing: '.08em', color: 'var(--env-solid)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{env.short}</span>
          </div>
        </div>
        <button type="button" className="adm__icon-btn" style={{ width: 28, height: 28 }} title={`Sign out of ${env.label}`} onClick={() => { signOut(env); signedOut(); }}><Icon name="logout" size={16} /></button>
      </div>
    </>
  );

  return (
    <Ctx.Provider value={ctx}>
      <div className={`adm ${mobile ? 'adm--mobile' : ''} ${density === 'compact' ? 'adm--compact' : ''}`} style={{ ...style, position: 'relative' }}>
        {!mobile && <aside className="adm__nav">{nav}</aside>}
        <div className="adm__main">
          <header className="adm__top">
            {mobile && <button type="button" className="adm__icon-btn" style={{ marginLeft: -6 }} aria-label="Menu" onClick={() => setNavOpen(true)}><Icon name="menu" size={20} /></button>}
            <div style={{ position: 'relative', flex: 'none' }}>
              <button type="button" className="adm__env" title="Switch environment" aria-haspopup="listbox" aria-expanded={envOpen} onClick={() => setEnvOpen((o) => !o)}>
                <span className="adm__env-dot" />{env.short}<Icon name="chevron" size={12} stroke={2.2} />
              </button>
              {envOpen && (
                <div className="adm__pop" role="listbox">
                  {ENVS.map((e) => {
                    const v = e.key === 'production' ? 'prod' : e.key === 'e2e' ? 'e2e' : 'local';
                    return (
                      <button key={e.key} type="button" role="option" aria-selected={e.key === env.key} className="adm__pop-row" onClick={() => { setEnv(e.key); setEnvOpen(false); setDrawers([]); }}>
                        <span style={{ flex: 'none', width: 10, height: 10, borderRadius: 3, background: `var(--env-${v})` }} />
                        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}><div style={{ fontSize: 13, fontWeight: 600 }}>{e.label}</div><div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-52)', marginTop: 2 }}>{e.host} · {e.appPrefix}*</div></div>
                        {e.key === env.key && <Icon name="check" size={15} stroke={2} style={{ color: 'var(--brand)' }} />}
                      </button>
                    );
                  })}
                  <div style={{ marginTop: 4, padding: '8px 10px 4px', borderTop: '1px solid var(--hair)', fontSize: 11, fontWeight: 300, lineHeight: 1.45, color: 'var(--ink-55)' }}>Each environment has its own API, database and sign-in. Full actions in every one; destructive confirmations name it.</div>
                </div>
              )}
            </div>
            {IS_LOCAL && <span title="make admin" style={{ flex: 'none', font: '600 9.5px var(--vs-font-mono)', letterSpacing: '.1em', padding: '4px 7px', borderRadius: 5, border: '1px dashed var(--ink-40)', color: 'var(--ink-70)' }}>LOCAL</span>}
            {mobile ? (
              mobileSearch
                ? <div style={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}><SearchBox env={env} inputRef={searchRef} q={search} setQ={setSearch} open={(s, d, p) => { setMobileSearch(false); open(s, d, p); }} autoFocus onBlur={() => { if (!search) setMobileSearch(false); }} /></div>
                : <>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cur.label}</span>
                    <button type="button" className="adm__icon-btn" aria-label="Search" onClick={() => setMobileSearch(true)}><Icon name="search" size={18} /></button>
                  </>
            ) : (
              <>
                <SearchBox env={env} inputRef={searchRef} q={search} setQ={setSearch} open={open} />
                <div style={{ flex: 1 }} />
                <button type="button" className={`adm__pill ${paused ? 'adm__pill--warn' : ''}`} title={paused ? `Polling paused (${pausedBy}). Panels show when they were last fresh.` : 'Fly panels every 5 s, database panels 10–30 s. Pauses on hidden tabs and after 30 min idle.'} onClick={() => setPausedBy((p) => (p ? '' : 'you'))}>
                  <span className="adm__dot" style={{ background: paused ? 'var(--conf-mid)' : 'var(--ok)' }} />{paused ? 'Paused · click to resume' : 'Live · polling'}
                </button>
                <div id="adm-top-slot" style={{ display: 'contents' }} />
              </>
            )}
            <button type="button" className="adm__icon-btn" title={theme === 'dark' ? 'Light theme' : 'Dark theme'} onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}><Icon name={theme === 'dark' ? 'sun' : 'moon'} size={18} /></button>
            {!mobile && <button type="button" className="adm__icon-btn" title={density === 'compact' ? 'Comfortable rows' : 'Compact rows'} onClick={() => setDensity(density === 'compact' ? 'comfortable' : 'compact')}><Icon name={density === 'compact' ? 'menu' : 'rows'} size={18} /></button>}
          </header>
          <main className="adm__scroll">
            <div className="adm__content">
              {schema && schema.state !== 'ok' && <SchemaBanner schema={schema} env={env} />}
              {mobile && !cur.mobile && (
                <div className="blk" style={{ background: 'var(--fill-05)', borderColor: 'transparent' }}><div style={{ display: 'flex', gap: 12, padding: '11px 14px' }}><Icon name="info" size={17} style={{ color: 'var(--ink-62)', flex: 'none' }} /><div><div style={{ fontSize: 13, fontWeight: 600 }}>Read-only on a phone</div><div style={{ fontSize: 12, fontWeight: 300, color: 'var(--ink-62)', marginTop: 2 }}>Actions on this screen are desktop-only. Open admin.vocalstack.com on a laptop to change anything.</div></div></div></div>
              )}
              {children(ctx)}
              <div style={{ height: 40 }} />
            </div>
          </main>
        </div>

        {envOpen && <div style={{ position: 'absolute', inset: 0, zIndex: 25 }} onClick={() => setEnvOpen(false)} />}

        {navOpen && (
          <div className="ov" style={{ zIndex: 45 }}>
            <div className="ov__scrim" onClick={() => setNavOpen(false)} />
            <aside className="adm__nav" style={{ position: 'relative', width: 280, maxWidth: '86%', height: '100%', display: 'flex', background: 'var(--vs-page)', animation: 'adm-nav .2s ease-out both' }}>{nav}</aside>
          </div>
        )}

        {top && (
          <div className="ov" style={{ zIndex: 40 }} role="dialog" aria-modal="true">
            <div className="ov__scrim" onClick={closeDrawer} />
            <DrawerFrame mobile={mobile} back={drawers.length > 1 ? backDrawer : undefined} close={closeDrawer}>{top.node}</DrawerFrame>
          </div>
        )}

        {modal && (
          <div className="ov" style={{ zIndex: 55, alignItems: mobile ? 'flex-end' : 'center', padding: mobile ? 0 : 32 }} role="dialog" aria-modal="true">
            <div className="ov__scrim" onClick={closeModal} />
            <FocusTrap>{modal}</FocusTrap>
          </div>
        )}

        {toastV && (
          <div className="toast" role="status">
            <div style={{ minWidth: 0, flex: '1 1 auto' }}><div style={{ fontSize: 13, fontWeight: 500, wordBreak: 'break-word' }}>{toastV.text}</div>{toastV.sub && <div style={{ fontSize: 11.5, fontWeight: 300, color: 'var(--ink-55)', marginTop: 2, wordBreak: 'break-word' }}>{toastV.sub}</div>}</div>
            {toastV.logged && <span className="toast__logged" title="Written to the audit log"><Icon name="check" size={12} stroke={2.4} />LOGGED</span>}
            {toastV.undo && <button type="button" className="btn btn--sm" style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }} onClick={() => { toastV.undo?.(); setToastV(null); }}>Undo · {Math.max(0, Math.ceil((toastV.until - Date.now()) / 1000))}s</button>}
            <button type="button" className="adm__icon-btn" style={{ width: 26, height: 26 }} aria-label="Dismiss" onClick={() => setToastV(null)}><Icon name="close" size={14} /></button>
          </div>
        )}
      </div>
    </Ctx.Provider>
  );
}

function SchemaBanner({ schema, env }: { schema: AdminSchema; env: Env }) {
  const ro = schema.readOnly;
  const title = ro ? `Read-only: ${env.label}'s database is behind this admin build`
    : schema.state === 'ahead' ? `${env.label}'s database is ahead of this admin build`
    : schema.state === 'mismatch' ? 'The database\'s schema baseline differs from this admin build\'s'
    : 'The database\'s migrations could not be checked';
  const color = ro ? 'var(--conf-low)' : 'var(--conf-mid)';
  return (
    <div className="blk" role={ro ? 'alert' : 'status'} style={{ background: ro ? 'var(--conf-low-soft)' : 'var(--conf-mid-soft)', borderColor: 'transparent' }}>
      <div style={{ display: 'flex', gap: 12, padding: '11px 14px' }}>
        <Icon name="warn" size={17} style={{ color, flex: 'none', marginTop: 1 }} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: 12, fontWeight: 300, color: 'var(--ink-70)', marginTop: 2, wordBreak: 'break-word' }}>{schema.message}</div>
          {ro && <div style={{ fontSize: 12, fontWeight: 300, color: 'var(--ink-62)', marginTop: 4 }}>Reads work; every action is refused until the release that ships {schema.missing.length === 1 ? 'it' : 'them'} runs (<span className="mono">make release</span>).</div>}
        </div>
      </div>
    </div>
  );
}

export function FocusTrap({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const q = () => Array.from(el.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter((x) => !x.hasAttribute('disabled'));
    const prev = document.activeElement as HTMLElement | null;
    (el.querySelector<HTMLElement>('[data-autofocus]') ?? q()[0])?.focus();
    const on = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const f = q();
      if (!f.length) return;
      const first = f[0]; const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    el.addEventListener('keydown', on);
    return () => { el.removeEventListener('keydown', on); prev?.focus?.(); };
  }, []);
  return <div ref={ref} style={{ display: 'contents' }}>{children}</div>;
}

function DrawerFrame({ children, back, close, mobile }: { children: ReactNode; back?: () => void; close: () => void; mobile: boolean }) {
  return (
    <FocusTrap>
      <div className="drawer" style={mobile ? { width: '100%' } : undefined} data-drawer>
        {back && <button type="button" className="adm__icon-btn" style={{ position: 'absolute', left: 10, top: 14, zIndex: 1 }} title="Back" onClick={back}><Icon name="back" size={16} /></button>}
        <button type="button" className="adm__icon-btn" style={{ position: 'absolute', right: 10, top: 14, zIndex: 1 }} title="Close" onClick={close}><Icon name="close" size={16} /></button>
        {children}
      </div>
    </FocusTrap>
  );
}

export function Drawer({ eyebrow, title, mono, sub, chip, acts, wide, children }: { eyebrow: string; title: ReactNode; mono?: boolean; sub?: ReactNode; chip?: ReactNode; acts?: ReactNode; wide?: boolean; children: ReactNode }) {
  useEffect(() => {
    const el = document.querySelector<HTMLElement>('[data-drawer]');
    if (el) el.classList.toggle('drawer--wide', !!wide);
  }, [wide]);
  return (
    <>
      <div className="drawer__head" style={{ paddingLeft: 52, paddingRight: 52 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="drawer__eyebrow">{eyebrow}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap', marginTop: 4 }}><span className={`drawer__title ${mono ? 'mono' : ''}`}>{title}</span>{chip}</div>
          {sub && <div className="drawer__sub">{sub}</div>}
        </div>
      </div>
      {acts && <div className="drawer__acts">{acts}</div>}
      <div className="drawer__body">{children}</div>
    </>
  );
}

export function Confirm({ env, app, title, body, guard, list, reason, typed, danger, confirmLabel, cancelLabel = 'Cancel', deskOnly, mobile, onConfirm, onClose, children, wide }: {
  env: Env; app?: string; title: ReactNode; body?: ReactNode; guard?: ReactNode; list?: [string, string][]; reason?: { label: string; placeholder?: string; min?: number }; typed?: string; danger?: boolean; confirmLabel?: string; cancelLabel?: string; deskOnly?: boolean; mobile: boolean; onConfirm?: (reason: string) => void | Promise<void>; onClose: () => void; children?: ReactNode; wide?: boolean;
}) {
  const [r, setR] = useState('');
  const [t, setT] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const typedOk = !typed || t.trim() === typed;
  const reasonOk = !reason || r.trim().length >= (reason.min ?? 3);
  const blocked = !!deskOnly && mobile;
  const ok = typedOk && reasonOk && !blocked && !busy;
  const v = env.key === 'production' ? 'prod' : env.key === 'e2e' ? 'e2e' : 'local';
  return (
    <div className={`modal ${wide ? 'modal--wide' : ''}`} style={mobile ? { borderRadius: '18px 18px 0 0', width: '100%' } : undefined}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="adm__env" style={{ height: 22, padding: '0 8px', fontSize: 9.5, background: `var(--env-${v})`, color: `var(--env-${v}-ink)` }}><span className="adm__env-dot" style={{ width: 5, height: 5 }} />{env.short}</span>
        {app && <span className="mono" style={{ fontSize: 11, color: 'var(--ink-55)', wordBreak: 'break-all' }}>{app}</span>}
      </div>
      <div className="modal__title">{title}</div>
      {body && <div className="modal__body">{body}</div>}
      {guard && <div className="modal__guard"><Icon name="warn" size={15} style={{ color: 'var(--conf-mid)', flex: 'none', marginTop: 1 }} /><span>{guard}</span></div>}
      {list && list.length > 0 && <div className="modal__list">{list.map(([k, val], i) => <div key={i} className="modal__kv"><span style={{ color: 'var(--ink-55)' }}>{k}</span><span style={{ wordBreak: 'break-all' }}>{val}</span></div>)}</div>}
      {children}
      {reason && (
        <label className="field"><span className="field__label">{reason.label}</span><input className="field__input" data-autofocus value={r} placeholder={reason.placeholder} onChange={(e) => setR(e.target.value)} /></label>
      )}
      {typed && (
        <label className="field">
          <span style={{ fontSize: 12, color: 'var(--ink-62)' }}>Type <span className="mono" style={{ fontWeight: 600, color: 'var(--ink-96)', wordBreak: 'break-all' }}>{typed}</span> to confirm in <span style={{ fontWeight: 600, color: `var(--env-${v})` }}>{env.label}</span></span>
          <input className="field__input mono" data-autofocus={!reason || undefined} value={t} onChange={(e) => setT(e.target.value)} style={{ borderColor: t && !typedOk ? 'var(--conf-low)' : typedOk && t ? 'var(--ok)' : undefined, fontSize: 13, fontWeight: 400 }} aria-label={`Type ${typed} to confirm`} />
        </label>
      )}
      {blocked && <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--conf-mid)' }}>This flow is desktop-only. Open admin.vocalstack.com on a laptop to continue.</div>}
      {err && <div className="login__err" role="alert"><Icon name="warn" size={15} style={{ color: 'var(--conf-low)', flex: 'none' }} /><span>{err}</span></div>}
      <div className="modal__acts">
        <button type="button" className="btn" style={{ height: 36 }} onClick={onClose}>{cancelLabel}</button>
        {confirmLabel && onConfirm && (
          <button type="button" className={`btn ${danger ? 'btn--solid-danger' : 'btn--primary'}`} style={{ height: 36 }} disabled={!ok} onClick={async () => { setBusy(true); setErr(null); try { await onConfirm(r.trim()); onClose(); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); } }}>{busy ? 'Working…' : confirmLabel}</button>
        )}
      </div>
    </div>
  );
}

export function Head({ title, sub, acts }: { title: string; sub?: ReactNode; acts?: ReactNode }) {
  const n = useContext(Ctx)?.attention?.counts.action ?? 0;
  useEffect(() => { document.title = `${n ? `(${n}) ` : ''}${title} · VocalStack Admin`; }, [title, n]);
  return (
    <div className="adm__head">
      <div style={{ flex: '1 1 320px', minWidth: 0 }}><h1>{title}</h1>{sub && <div className="adm__sub">{sub}</div>}</div>
      {acts && <div style={{ flex: 'none', display: 'flex', gap: 6, flexWrap: 'wrap' }}>{acts}</div>}
    </div>
  );
}

interface SearchRow { title: string; sub: string; drawer: string; screen: string }
interface SearchGroup { kind: string; label: string; total: number; rows: SearchRow[] }

function SearchBox({ env, inputRef, q, setQ, open, autoFocus, onBlur }: { env: Env; inputRef: React.RefObject<HTMLInputElement>; q: string; setQ: (q: string) => void; open: (screen: string, drawer?: string, params?: Record<string, string>) => void; autoFocus?: boolean; onBlur?: () => void }) {
  const [groups, setGroups] = useState<SearchGroup[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(false);
  const [active, setActive] = useState(0);
  const term = q.trim();
  useEffect(() => {
    if (term.length < 2) { setGroups(null); return; }
    let live = true;
    setBusy(true);
    const id = setTimeout(() => {
      get<{ groups: SearchGroup[] }>(env, `/admin/search?q=${encodeURIComponent(term)}`)
        .then((r) => { if (live) { setGroups(r.groups); setActive(0); } })
        .catch(() => { if (live) setGroups([]); })
        .finally(() => { if (live) setBusy(false); });
    }, 250);
    return () => { live = false; clearTimeout(id); };
  }, [env, term]);
  const flat = (groups ?? []).flatMap((g) => g.rows);
  const pick = (r: SearchRow) => { setShown(false); setQ(''); inputRef.current?.blur(); open(r.screen, r.drawer); };
  let i = -1;
  return (
    <div style={{ position: 'relative', flex: '0 1 380px', minWidth: 120, display: 'flex' }}>
      <div className="adm__search" style={{ flex: 1, maxWidth: 'none' }}>
        <Icon name="search" size={14} style={{ color: 'var(--ink-45)' }} />
        <input ref={inputRef} value={q} autoFocus={autoFocus} placeholder="Search customers, keys, tickets, ids, machines   ⌘K" aria-label="Search" role="combobox" aria-expanded={shown && !!groups} aria-controls="adm-search-results"
          onChange={(e) => { setQ(e.target.value); setShown(true); }} onFocus={() => setShown(true)} onBlur={() => { setTimeout(() => setShown(false), 150); onBlur?.(); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(flat.length - 1, a + 1)); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
            else if (e.key === 'Enter' && flat[active]) { e.preventDefault(); pick(flat[active]); }
            else if (e.key === 'Escape') { e.stopPropagation(); setShown(false); inputRef.current?.blur(); }
          }} />
        {busy && <span className="mono" style={{ fontSize: 10, color: 'var(--ink-45)' }}>…</span>}
      </div>
      {shown && groups && term.length >= 2 && (
        <div className="srch" id="adm-search-results" role="listbox">
          {!groups.length && <div style={{ padding: '10px 12px', fontSize: 12.5, color: 'var(--ink-55)' }}>Nothing matches “{term}”.</div>}
          {groups.map((g) => (
            <div key={g.kind}>
              <div className="srch__group">{g.label.toUpperCase()}{g.total > g.rows.length ? ` · ${g.rows.length} OF ${g.total}` : ''}</div>
              {g.rows.map((r) => {
                i += 1;
                const on = i === active;
                return (
                  <button key={r.drawer} type="button" role="option" aria-selected={on} className={`srch__row ${on ? 'srch__row--on' : ''}`} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(r)}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-96)', wordBreak: 'break-all' }}>{r.title}</span>
                    {r.sub && <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-52)', wordBreak: 'break-all' }}>{r.sub}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SaveView() {
  const ctx = useAdmin();
  const cur = SCREENS.find((s) => s.key === ctx.screen);
  const has = Object.keys(ctx.params).length > 0;
  const exists = ctx.views.find((v) => v.screen === ctx.screen && sameParams(v.params, ctx.params));
  if (!has || exists) return null;
  return <button type="button" className="chip" title="Keep these filters in the navigation" onClick={() => ctx.openModal(<SaveViewModal screen={ctx.screen} label={cur?.label ?? ctx.screen} params={ctx.params} />)}>Save view</button>;
}

function SaveViewModal({ screen, label, params }: { screen: string; label: string; params: Record<string, string> }) {
  const ctx = useAdmin();
  const [name, setName] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await ctx.saveViews([...ctx.views, { id: Math.random().toString(36).slice(2, 10), screen, name: n, params }]);
      ctx.closeModal();
      ctx.toast(`Saved “${n}” under ${label}`, undefined, { logged: false });
    } catch (e) { setErr((e as Error).message); }
  };
  return (
    <div className="modal" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="modal__title">Save this view</div>
      <div className="modal__body">It shows under {label} in the navigation, with these filters: <span className="mono" style={{ wordBreak: 'break-all' }}>{new URLSearchParams(params).toString().replace(/&/g, ' · ')}</span></div>
      <Field label="Name" value={name} onChange={setName} placeholder="Past due, Failed this week…" autoFocus onEnter={() => void save()} />
      {err && <div style={{ fontSize: 12, color: 'var(--conf-low)' }}>{err}</div>}
      <div className="modal__acts">
        <button type="button" className="btn btn--sm" onClick={ctx.closeModal}>Cancel</button>
        <button type="button" className="btn btn--sm btn--primary" disabled={!name.trim()} onClick={() => void save()}>Save view</button>
      </div>
    </div>
  );
}
